import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Trash2 } from "lucide-react";
import { API, auth, handleAuthError } from "./shared";

const SUGGESTIONS = ["Comment envoyer une facture à un client sans e-mail ?", "Combien de devis ai-je reçus ce mois ?", "Comment masquer une section du site ?", "Rédige une annonce de promotion pour la climatisation"];

const AdminHelp = ({ token, logout }) => {
  const [messages, setMessages] = useState(() => { try { return JSON.parse(sessionStorage.getItem("gal-admin-help") || "[]"); } catch { return []; } });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { sessionStorage.setItem("gal-admin-help", JSON.stringify(messages.slice(-30))); endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.slice(-8).map(({ role, text: t }) => ({ role, text: t }));
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/admin-help`, { message: msg, history }, auth(token));
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "L'assistant IA est indisponible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded-3xl border border-forest/15 bg-forest p-8 text-white" data-testid="admin-help">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display flex items-center gap-2 text-lg font-bold"><Sparkles className="h-5 w-5 text-gold" /> Aide IA pour les responsables</h2>
          <p className="mt-1 max-w-2xl text-sm text-white/70">Posez toutes vos questions : utilisation de l'espace entreprise, chiffres de l'activité, rédaction d'annonces ou de réponses, conseils de gestion. L'assistant connaît chaque onglet et vos données du jour.</p>
        </div>
        {messages.length > 0 && <button type="button" onClick={() => setMessages([])} data-testid="admin-help-clear" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white/80 hover:bg-white/10"><Trash2 className="h-3.5 w-3.5" /> Effacer</button>}
      </div>

      <div className="mt-6 max-h-[60vh] min-h-[240px] space-y-3 overflow-y-auto rounded-2xl bg-white/5 p-4" data-testid="admin-help-messages">
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2" data-testid="admin-help-suggestions">
            {SUGGESTIONS.map((s) => <button type="button" key={s} onClick={() => send(s)} className="rounded-full border border-white/20 px-3 py-1.5 text-xs text-white/90 transition-colors hover:border-gold hover:text-gold">{s}</button>)}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`admin-help-msg-${i}`}>
            <p className={`max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-md bg-gold text-white" : "rounded-bl-md bg-white text-forest"}`}>{m.text}</p>
          </div>
        ))}
        {loading && <p className="inline-flex items-center gap-2 text-xs text-white/70" data-testid="admin-help-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> L'assistant réfléchit…</p>}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Votre question…" data-testid="admin-help-input" className="w-full rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold" />
        <button type="button" onClick={() => send()} disabled={loading || !input.trim()} data-testid="admin-help-send" aria-label="Envoyer" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold text-white transition-colors hover:bg-gold-hover disabled:opacity-50"><Send className="h-4 w-4" /></button>
      </div>
    </section>
  );
};

export default AdminHelp;
